import { useState, useEffect } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import axios from "axios";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.mjs`;

const API_BASE_URL = process.env.REACT_APP_FASTAPI_URL;

export default function Home() {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [jsonData, setJsonData] = useState("");
  const [countdown, setCountdown] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      setSnackbar({
        open: true,
        message: "Please select a valid PDF file.",
        severity: "warning",
      });
    }
  };
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) return;

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // ✅ Handle API call
  const handleConvert = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Please select a PDF file first.",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      setCountdown(15); // Set countdown to 30 seconds
      // Prepare FormData for upload
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("jsonData", jsonData);
      const response = await axios.post(
        `${API_BASE_URL}/api/ai/convert`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message || "Conversion successful!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error calling backend:", error);
      setSnackbar({
        open: true,
        message: "Failed to convert PDF.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "83.5vh",
        }}
      >
        {/* Top Row - PDF Upload + JSON Input */}
        <Box sx={{ display: "flex", flexGrow: 1, gap: 2 }}>
          {/* Left Side - PDF Upload */}
          <Paper
            sx={{
              flexGrow: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                border: "2px dashed #81c784",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                mt: 1,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                "&:hover": { backgroundColor: "#f9fff9" },
              }}
              onClick={() => document.getElementById("fileInput").click()}
            >
              {selectedFile ? (
                <>
                  <Typography variant="h6">PDF Preview</Typography>

                  <Box
                    sx={{
                      width: "100%",
                      maxHeight: "300px",
                      overflow: "auto",
                      border: "1px solid #ccc",
                      borderRadius: 2,
                      p: 1,
                    }}
                  >
                    <Document
                      file={selectedFile}
                      onLoadError={(err) => console.error("PDF Error:", err)}
                    >
                      <Page pageNumber={1} width={300} />
                    </Document>
                  </Box>
                </>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{ fontSize: 50, color: "#2e7d32", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Click to Upload PDF
                  </Typography>
                </>
              )}

              {/* Hidden File Input */}
              <input
                id="fileInput"
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </Box>
          </Paper>

          {/* Right Side - JSON Input */}
          <Paper
            sx={{
              flexGrow: 2,
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextField
              fullWidth
              multiline
              placeholder="Paste the list of fields to be replaced, separated by commas.(invoice_no, supplier_name)"
              variant="outlined"
              onChange={(e) => setJsonData(e.target.value)}
              value={jsonData}
              sx={{
                flexGrow: 1,
                fontFamily: "monospace",
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "flex-start",
                },
                "& textarea": {
                  height: "100%",
                },
              }}
            />
          </Paper>
        </Box>

        {/* Bottom Row - Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end", // change to "center" if you want centered button
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={handleConvert}
            disabled={!(selectedFile && jsonData.trim())}
          >
            Generate Template
          </Button>
        </Box>
      </Box>

      {/* Loader Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h2" sx={{ fontWeight: "bold" }}>
          {countdown}
        </Typography>
        <Typography>Generating Template...</Typography>
      </Backdrop>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
